import React, { useCallback, useContext, useRef, useState } from 'react';
import { IRuleStepModel } from 'domains/designers/RuleDesigner/models/RuleStepModel';
import { Cell } from './Cell';
//TODO alex: should we move "setTruthTableStepDisabled" to TruthTableEditService?
import {
  setTruthTableStepDisabled,
  getEditContextForStep,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import { ShowContextEditorLayerCallback } from 'domains/designers/TruthTable/TruthTable';
import { PhraseColumn } from '../columns';
import { getRowSpan } from '../TruthTableUtils';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';
import classNames from 'classnames';
import {
  mergeCellBelow,
  mergeCellAbove,
  getCellSettings,
  unmergeBottomCell,
  unmergeTopCell,
  unmergeAllCells,
  setOtherwise,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { CellSettings } from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/CellSettings';
import { HasOpenState } from 'hooks/HasOpenState';
import { IActionLinkItem } from 'components/ActionLinks/ActionLinkModels';
import { conditionalArray } from 'utils/ArrayUtils';
import { PhraseItemClickEvent } from '../header-cells/HeaderPhraseCell';
import { ContextMenu, VerbAndValue } from '../components';
import { StepEvaluation } from 'domains/designers/RuleDesigner/components';
import './ValueCell.less';
import { IRootGroupModel } from 'domains/designers/RuleDesigner/models/RootGroupModel';

interface IValueCellProps extends React.HTMLProps<HTMLTableCellElement> {
  index: number;
  rootModel: IRootGroupModel;
  ruleStep: IRuleStepModel;
  showContextEditorLayer: ShowContextEditorLayerCallback;
  onVerbClick?: (event: PhraseItemClickEvent<IRuleStepModel>) => void;
  onUseDefaultVerbClick?: (step: IRuleStepModel) => void;
}

/**
 * Represent a Truth Table Cell
 * 3rd part of phrase - Value (+ Specified Verb, if it was overridden)
 * Phrase consist of 3 parts: Anchor, Verb, Value.
 * Anchor and Verb are shown in [[HeaderPhraseCell]]
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ValueCell({
  index,
  rootModel,
  showContextEditorLayer,
  ruleStep,
  className,
  onVerbClick,
  onUseDefaultVerbClick,
  ...otherProps
}: IValueCellProps) {
  const { isDebug } = useContext(RuleDesignerContext);
  const sessionId = rootModel.RuleSessionId;
  const editCellRef = useRef<HTMLButtonElement | null>(null);
  const [cellSettings, setCellSettings] = useState<CellSettings | null>(null);
  const { isOpen, setClosed, setOpen } = HasOpenState(false);
  const [isLoadingActions, setLoadingActions] = useState(false);

  const ignoreStep = useCallback(() => {
    setClosed();

    return setTruthTableStepDisabled({
      ruleStepId: ruleStep.Id,
      ruleSessionId: sessionId,
      isDisabled: true,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const dontIgnoreStep = useCallback(() => {
    setClosed();

    return setTruthTableStepDisabled({
      ruleStepId: ruleStep.Id,
      ruleSessionId: sessionId,
      isDisabled: false,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const mergeAbove = useCallback(() => {
    setClosed();

    mergeCellAbove({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const mergeBelow = useCallback(() => {
    setClosed();

    mergeCellBelow({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const handleUnmergeAllCells = useCallback(() => {
    setClosed();

    unmergeAllCells({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const handleOtherwise = useCallback(() => {
    setClosed();

    setOtherwise({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const handleUnmergeBottom = useCallback(() => {
    setClosed();

    unmergeBottomCell({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const handleUnmergeTop = useCallback(() => {
    setClosed();

    unmergeTopCell({
      ruleSessionId: sessionId,
      stepId: ruleStep.Id,
    });
  }, [ruleStep.Id, sessionId, setClosed]);

  const showContextMenu: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async e => {
      editCellRef.current = e.target as HTMLButtonElement;

      try {
        setLoadingActions(true);
        setOpen();

        const cellSettings = await getCellSettings({
          ruleSessionId: sessionId,
          stepId: ruleStep.Id,
        });
        setCellSettings(cellSettings);
      } finally {
        setLoadingActions(false);
      }
    },
    [ruleStep.Id, sessionId, setOpen]
  );

  const rowSpan = getRowSpan(rootModel, ruleStep.Id);

  const handleVerbClick = (target: HTMLButtonElement) => {
    //TODO: "actions - 3 dots" icon should be visible when click "Override Verb"
    !isDebug &&
      onVerbClick?.({
        target,
        header: ruleStep,
      });
  };

  const handleOverrideVerbAction = useCallback(() => {
    setClosed();
    !isDebug &&
      onVerbClick?.({
        target: editCellRef.current as HTMLButtonElement,
        header: ruleStep,
      });
  }, [isDebug, onVerbClick, ruleStep, setClosed]);

  const handleUseDefaultVerbAction = useCallback(() => {
    setClosed();
    !isDebug && onUseDefaultVerbClick?.(ruleStep);
  }, [isDebug, onUseDefaultVerbClick, ruleStep, setClosed]);

  if (rowSpan === 0) {
    return null;
  }

  return (
    <PhraseColumn
      className={classNames(className, 'tt-value-cell', {
        'tt-value-cell--ignored': ruleStep.DisplayData.IsIgnored,
        'tt-value-cell--first': index === 0,
      })}
      {...otherProps}
      rowSpan={rowSpan}
    >
      <Cell>
        <VerbAndValue
          ruleStep={ruleStep}
          onEditClick={e =>
            showContextEditorLayer({
              target: e.target as HTMLElement,
              getContext: () =>
                getEditContextForStep({
                  ruleSessionId: sessionId,
                  ruleStepId: ruleStep.Id,
                }),
              canIgnore: true,
              onIgnore: ignoreStep,
            })
          }
          onActionsClick={showContextMenu}
          actionsProps={{
            isActive: isOpen,
            isDisabled: isLoadingActions,
          }}
          onVerbClick={handleVerbClick}
        />
        {ruleStep.Debug?.evaluation && ruleStep.DisplayData.HasAnchor && (
          <StepEvaluation evaluation={ruleStep.Debug.evaluation} showBeyondPass={true} />
        )}
        {!isDebug && (
          <ContextMenu
            isOpen={isOpen}
            onClose={() => {
              setCellSettings(null);
              setClosed();
            }}
            sourceRef={editCellRef}
            placement="right"
            isLoading={isLoadingActions}
            actions={conditionalArray<IActionLinkItem>(
              [
                {
                  label: 'Ignore Condition',
                  value: 'Ignore',
                  onClick: ignoreStep,
                },
                !ruleStep.DisplayData.IsIgnored,
              ],
              [
                {
                  label: "Don't Ignore Condition",
                  value: 'dont ignore',
                  onClick: dontIgnoreStep,
                },
                ruleStep.DisplayData.IsIgnored,
              ],
              [
                {
                  label: 'Override Default Verb',
                  value: 'OverrideVerb',
                  onClick: handleOverrideVerbAction,
                },
                cellSettings?.CanOverrideStep === true,
              ],
              [
                {
                  label: 'Use Default Verb',
                  value: 'UseDefaultVerb',
                  onClick: handleUseDefaultVerbAction,
                },
                cellSettings?.CanSetDefaultVerb === true,
              ],
              [
                {
                  label: 'Merge Above',
                  value: 'Merge Above',
                  onClick: mergeAbove,
                },
                cellSettings?.CanMergeAbove === true,
              ],
              [
                {
                  label: 'Merge Below',
                  value: 'Merge Below',
                  onClick: mergeBelow,
                },
                cellSettings?.CanMergeBelow === true,
              ],
              [
                {
                  label: 'Unmerge Top Cell',
                  value: 'Unmerge Top Cell',
                  onClick: handleUnmergeTop,
                },
                cellSettings?.CanUnmergeAbove === true,
              ],
              [
                {
                  label: 'Unmerge Bottom Cell',
                  value: 'Unmerge Bottom Cell',
                  onClick: handleUnmergeBottom,
                },
                cellSettings?.CanUnmergeBelow === true,
              ],
              [
                {
                  label: 'Unmerge All Cells',
                  value: 'Unmerge All Cells',
                  onClick: handleUnmergeAllCells,
                },
                cellSettings?.CanUnmergeAll === true,
              ],
              [
                {
                  label: 'Set Otherwise',
                  value: 'otherwise',
                  onClick: handleOtherwise,
                },
                cellSettings?.CanSetOtherwise === true,
              ]
            )}
          />
        )}
      </Cell>
    </PhraseColumn>
  );
}
